// The AI gap report itself, header aside. It used to live in the live-session
// ReportPage; that page went with the live-session audit (TYR-216) and the
// markup moved here, so a stored client scan still renders its saved report.

import { motion } from 'framer-motion';
import { AlertOctagon, ArrowRight, CheckCircle2 } from 'lucide-react';
import { RiskBadge } from '../ui/RiskBadge';
import { PillarBar } from '../ui/PillarBar';
import type {
  AIReport,
  CriticalIssue,
  Effort,
  GrowthOpportunity,
  QuickWin,
  Severity,
} from '../../types/report.types';

const PRIORITY_TONE: Record<Severity, 'critical' | 'high' | 'medium' | 'low'> = {
  Critical: 'critical',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
};

const EFFORT_LABEL: Record<Effort, string> = {
  S: 'S · ≤1 wk',
  M: 'M · 2 to 4 wk',
  L: 'L · 1 to 2 mo',
  XL: 'XL · 3 mo+',
};

export function GapReportBody({ report }: { report: AIReport }) {
  const breakdown = report.healthBreakdown;
  const breakdownRows: { key: keyof typeof breakdown; label: string; max: number; color: 'deliver' | 'design' | 'adopt' | 'sunrise' | 'grow' }[] = [
    { key: 'automation', label: 'Automation', max: 25, color: 'deliver' },
    { key: 'dataQuality', label: 'Data Quality', max: 20, color: 'design' },
    { key: 'security', label: 'Security', max: 25, color: 'adopt' },
    { key: 'metadata', label: 'Metadata', max: 15, color: 'sunrise' },
    { key: 'users', label: 'Users', max: 15, color: 'grow' },
  ];

  return (
    <>
      {/* ── Executive summary ──────────────────────────────────────────── */}
      <motion.section
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-surface-1 border border-border rounded-lg p-6"
      >
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
          Executive Summary
        </div>
        <p className="text-base text-crux-light leading-relaxed">{report.executiveSummary}</p>
      </motion.section>

      {/* ── Health breakdown rationale ──────────────────────────────────── */}
      <section className="bg-surface-1 border border-border rounded-lg p-6">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mb-4">
          Health Score Rationale
        </div>
        <div className="space-y-5">
          {breakdownRows.map(({ key, label, max, color }) => {
            const cat = breakdown[key];
            return (
              <div key={key}>
                <PillarBar label={label} score={cat.score} max={max} color={color} />
                <p className="text-sm text-neutral-400 mt-2 ml-32 leading-snug">{cat.rationale}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Critical issues ─────────────────────────────────────────────── */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Critical Issues
          </div>
          <div className="text-xs text-neutral-600">{report.criticalIssues.length}</div>
        </div>
        {report.criticalIssues.length === 0 ? (
          <div className="bg-surface-2 border border-border rounded-lg p-6 text-sm text-neutral-500">
            No critical issues flagged.
          </div>
        ) : (
          <div className="space-y-3">
            {report.criticalIssues.map((issue, i) => (
              <CriticalIssueCard key={i} issue={issue} />
            ))}
          </div>
        )}
      </section>

      {/* ── Quick wins ─────────────────────────────────────────────────── */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Quick Wins · 30-day fixes
          </div>
          <div className="text-xs text-neutral-600">{report.quickWins.length}</div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {report.quickWins.map((win, i) => (
            <QuickWinCard key={i} win={win} />
          ))}
        </div>
      </section>

      {/* ── Growth opportunities ───────────────────────────────────────── */}
      <section>
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            Growth Opportunities · TYRL upsell
          </div>
          <div className="text-xs text-neutral-600">{report.growthOpportunities.length}</div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {report.growthOpportunities.map((gap, i) => (
            <GapCard key={i} gap={gap} />
          ))}
        </div>
      </section>

      {/* ── Recommended engagement ─────────────────────────────────────── */}
      <section className="bg-grow/5 border border-grow/30 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-md bg-grow/15 border border-grow/40 text-grow flex items-center justify-center shrink-0">
            <ArrowRight size={18} />
          </span>
          <div className="flex-1">
            <div className="text-[11px] uppercase tracking-widest text-grow">
              Recommended Engagement
            </div>
            <div className="text-lg font-semibold text-crux-light mt-1">
              {report.recommendedEngagement.primaryService}
              <span className="text-sm font-normal text-neutral-400 ml-2">
                · {report.recommendedEngagement.estimatedScope}
              </span>
            </div>
            <p className="text-sm text-crux-light/85 mt-2 leading-relaxed">
              {report.recommendedEngagement.rationale}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function CriticalIssueCard({ issue }: { issue: CriticalIssue }) {
  return (
    <div className="bg-surface-1 border border-sunset/30 rounded-lg p-5">
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-md bg-sunset/15 border border-sunset/40 text-sunset flex items-center justify-center shrink-0">
          <AlertOctagon size={16} />
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-base font-semibold text-crux-light">{issue.title}</h3>
            <RiskBadge tone={issue.severity === 'Critical' ? 'critical' : 'high'}>
              {issue.severity}
            </RiskBadge>
          </div>
          <p className="text-sm text-neutral-400 mt-2 leading-relaxed">{issue.description}</p>
          <p className="text-sm text-sunset mt-2 leading-snug">
            <span className="text-[10px] uppercase tracking-widest text-sunset/80 mr-2">
              Impact
            </span>
            {issue.estimatedImpact}
          </p>
        </div>
      </div>
    </div>
  );
}

function QuickWinCard({ win }: { win: QuickWin }) {
  return (
    <div className="bg-surface-1 border border-grow/30 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <span className="w-7 h-7 rounded-md bg-grow/15 border border-grow/40 text-grow flex items-center justify-center shrink-0 mt-0.5">
          <CheckCircle2 size={14} />
        </span>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-crux-light">{win.title}</h3>
            <span className="text-[10px] uppercase tracking-widest text-grow shrink-0">
              {win.estimatedTime}
            </span>
          </div>
          <p className="text-xs text-neutral-400 mt-1.5 leading-snug">{win.description}</p>
        </div>
      </div>
    </div>
  );
}

function GapCard({ gap }: { gap: GrowthOpportunity }) {
  return (
    <div className="bg-surface-1 border border-border rounded-lg p-5 hover:border-neutral-600 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-base font-semibold text-crux-light flex-1">{gap.gap}</h3>
        <RiskBadge tone={PRIORITY_TONE[gap.priority]}>{gap.priority}</RiskBadge>
      </div>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-neutral-500 mb-3">
        <span className="text-grow">{gap.service}</span>
        <span>·</span>
        <span>{EFFORT_LABEL[gap.effort]}</span>
      </div>
      <p className="text-sm text-neutral-400 leading-snug">{gap.impact}</p>
    </div>
  );
}
